import React, { useCallback, useMemo } from 'react'

import { ImageList, ImageListItem, ImageListItemBar } from '@mui/material'
import {
  CreateButton,
  Link,
  List,
  ListProps,
  SearchInput,
  SelectInput,
  TopToolbar,
  Translate,
  useCreatePath,
  useListContext,
  useTranslate,
} from 'react-admin'

import { Tooltip } from '@mantine/core'
import { API } from '../../helpers/Api.helper'
import { useUserStore } from '../../stores/useUserStore'
import { Forbidden } from '../Forbidden'
import { LXImage } from './../../components/LXImage/LXImage'
import { Beneficiary } from './../../types/Beneficiary'
import styles from './BeneficiaryList.module.scss'

const BeneficiaryGrid = (): JSX.Element => {
  const { data } = useListContext<Beneficiary>()
  const createPath = useCreatePath()

  const createBeneficiaryPath = useCallback(
    (id: Beneficiary['id']) =>
      createPath({
        resource: 'beneficiaries',
        id,
        type: 'show',
      }),
    [createPath],
  )

  return (
    <ImageList className={styles.container} cols={5}>
      {data?.map(beneficiary => (
        <ImageListItem key={beneficiary.id}>
          <Link to={createBeneficiaryPath(beneficiary.id)}>
            <LXImage source="logoBase64" gridSize={12} record={beneficiary} />
            <ImageListItemBar title={beneficiary.name} />
          </Link>
        </ImageListItem>
      ))}
    </ImageList>
  )
}

type BeneficiaryListActionsProps = {
  isAuthorised: boolean
  translate: Translate
}
const BeneficiaryListActions = ({
  isAuthorised,
  translate,
}: BeneficiaryListActionsProps): JSX.Element => (
  <TopToolbar>
    <Tooltip
      label={`⚠ ${translate('pages.users.lackingPermission')} → ${translate(
        'pages.users.permissionsList.beneficiaries.edit',
      )}`}
      hidden={isAuthorised}
    >
      <span>
        <CreateButton disabled={!isAuthorised} />
      </span>
    </Tooltip>
  </TopToolbar>
)

export const BeneficiaryList = (props: ListProps): JSX.Element => {
  const translate = useTranslate()
  const userStore = useUserStore()

  const cmsList = useMemo(() => userStore.getCMSList(), [userStore])

  if (!userStore.isAuthorised('beneficiaries.read')) {
    return <Forbidden permission="beneficiaries.read" />
  }

  return (
    <List
      {...props}
      actions={
        <BeneficiaryListActions
          isAuthorised={userStore.isAuthorised('beneficiaries.edit')}
          translate={translate}
        />
      }
      perPage={API.DEFAULT_PAGINATION.perPage}
      filters={[
        <SearchInput key="search" source="q" alwaysOn />,
        <SelectInput key="cms" source="cms" choices={cmsList} alwaysOn />,
      ]}
      sort={{ field: 'name', order: 'ASC' }}
    >
      <BeneficiaryGrid />
    </List>
  )
}
